import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { StaleClientError } from '@immo/api-contract';
import { ErrorPage } from '@/components/errors/error-page';
import { OutdatedView } from '@/components/errors/outdated-view';
import { ApiError, ForbiddenError, NotFoundError } from '@/lib/errors/api-error';
import { toCapturableError } from '@/lib/errors/capturable-error';
import { resolveErrorSurface } from '@/lib/errors/display-policy';
import { captureFrontendError } from '@/lib/errors/error-context';
import { NotFoundPage } from './not-found';

/**
 * Route-level `errorElement` — rendered by React Router whenever a route's
 * loader, action, or element throws. Per ADR 0014 (amended 2026-05-08) this
 * is the SPA equivalent of App Router's per-segment `error.tsx`.
 *
 * A typed `ApiError` (thrown after `parseApiError`) is rendered on its
 * class-driven surface; anything else is a frontend-origin failure captured
 * with a client-minted id and shown as the generic reportable page.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    if (error instanceof ApiError) return;
    captureFrontendError(toCapturableError(error), 'route-error-element');
  }, [error]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  // Most of the app's requests are made from loaders, so this — not the root
  // boundary — is where a stale bundle's parse failure actually lands.
  if (error instanceof StaleClientError) {
    return <OutdatedView />;
  }

  if (error instanceof NotFoundError) {
    return <NotFoundPage />;
  }

  if (error instanceof ForbiddenError) {
    return <ErrorPage code={error.code} details={error.details} />;
  }

  if (error instanceof ApiError) {
    const retryProps = resolveErrorSurface(error) === 'toast-reportable' ? { onRetry: () => navigate(0) } : {};

    return <ErrorPage code={error.code} id={error.id} details={error.details} {...retryProps} />;
  }

  return <ErrorPage onRetry={() => navigate(0)} />;
}
